import { useEffect, useState } from 'react';
import { Alert, Button, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { CheckCircle2, XCircle } from 'lucide-react';
import type { OauthMessage } from './OauthPopupHelper';

type CallbackStatus = 'pending' | 'success' | 'error';

function postToOpener(message: OauthMessage): void {
    if (window.opener && !window.opener.closed) {
        window.opener.postMessage(message, window.location.origin);
    }
}

export default function OauthCallbackPage(): React.ReactElement {
    const [status, setStatus] = useState<CallbackStatus>('pending');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const adminConsent = params.get('admin_consent');
        const providerError = params.get('error_description') ?? params.get('error');
        const errorSubcode = params.get('error_subcode') ?? undefined;

        if (providerError) {
            const errMsg: { type: 'oauth:error'; error: string; errorSubcode?: string } = {
                type: 'oauth:error',
                error: providerError,
            };
            if (errorSubcode !== undefined) errMsg.errorSubcode = errorSubcode;
            postToOpener(errMsg);
            setStatus('error');
            setErrorMessage(providerError);
            return;
        }

        if ((adminConsent === 'True' || adminConsent === 'true') && state) {
            postToOpener({ type: 'oauth:admin-consent-granted', state });
            setStatus('success');
            window.setTimeout(() => window.close(), 600);
            return;
        }

        if (!code || !state) {
            const message = 'OAuth callback is missing required parameters.';
            postToOpener({ type: 'oauth:error', error: message });
            setStatus('error');
            setErrorMessage(message);
            return;
        }

        postToOpener({ type: 'oauth:complete', code, state });
        setStatus('success');
        window.setTimeout(() => window.close(), 600);
    }, []);

    return (
        <Center h="100vh" p="md">
            <Stack align="center" gap="md" maw={420}>
                {status === 'pending' && (
                    <>
                        <Loader />
                        <Title order={4}>Finalizing authorization…</Title>
                        <Text size="sm" c="dimmed">Please keep this window open for a moment.</Text>
                    </>
                )}
                {status === 'success' && (
                    <Alert variant="light" color="green" icon={<CheckCircle2 size={16} />} title="Connected">
                        <Text size="sm">Authorization complete. You can close this window.</Text>
                    </Alert>
                )}
                {status === 'error' && (
                    <Stack align="stretch" gap="sm" w="100%">
                        <Alert variant="light" color="red" icon={<XCircle size={16} />} title="Authorization failed">
                            <Text size="sm">{errorMessage}</Text>
                        </Alert>
                        <Button variant="default" onClick={() => window.close()}>Close window</Button>
                    </Stack>
                )}
            </Stack>
        </Center>
    );
}
